import {
  useState,
  createContext,
  useContext,
  type ReactNode,
  useCallback,
  useRef,
} from 'react'

interface ToastMessage {
  id: number
  message: string
  type: 'success' | 'error'
}

interface ToastContextType {
  showSuccessMessage: (message: string) => void
  showErrorMessage: (message: string) => void
  reset: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

const TOAST_COLORS = {
  error: '#c9302c',
  success: '#0acf97',
} as const

function ToastIcon({ type }: { type: 'success' | 'error' }) {
  const color = TOAST_COLORS[type]
  if (type === 'error') {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 16 16"
        fill={color}
        aria-hidden
      >
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
        <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
      </svg>
    )
  }
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 16"
      fill={color}
      aria-hidden
    >
      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
    </svg>
  )
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [currentToast, setCurrentToast] = useState<ToastMessage | null>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
    const id = Date.now() + Math.random()
    setCurrentToast({ id, message, type })
    const duration = type === 'error' ? 8000 : 3000
    hideTimeoutRef.current = setTimeout(() => {
      setCurrentToast(null)
      hideTimeoutRef.current = null
    }, duration)
  }, [])

  const showSuccessMessage = (message: string): void => {
    showToast(message, 'success')
  }

  const showErrorMessage = (message: string): void => {
    showToast(message, 'error')
  }

  const reset = (): void => {
    setCurrentToast(null)
  }

  const contextValue: ToastContextType = {
    showSuccessMessage,
    showErrorMessage,
    reset,
  }

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {currentToast && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              pointerEvents: 'auto',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              padding: 0,
              overflow: 'hidden',
              minWidth: '320px',
              maxWidth: '400px',
              animation: 'town-toast-slideIn 0.3s ease-out',
            }}
          >
            <style>
              {`
                @keyframes town-toast-slideIn {
                  from { transform: translateX(100%); opacity: 0; }
                  to { transform: translateX(0); opacity: 1; }
                }
              `}
            </style>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
                gap: '12px',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  backgroundColor: TOAST_COLORS[currentToast.type],
                }}
              />
              <span style={{ flexShrink: 0, display: 'flex' }}>
                <ToastIcon type={currentToast.type} />
              </span>
              <div
                style={{
                  flex: 1,
                  color: '#4A4A4A',
                  fontSize: '14px',
                  lineHeight: 1.5,
                }}
              >
                {currentToast.message}
              </div>
              <button
                type="button"
                onClick={() => setCurrentToast(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  flexShrink: 0,
                  width: 24,
                  height: 24,
                }}
                aria-label="閉じる"
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
